import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/lib/api';


axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = loading, false = not authed, object = authed
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        // Don't let axios throw/log on 401 (expected when no session cookie)
        validateStatus: (s) => (s >= 200 && s < 300) || s === 401,
      });
      if (data && data.email) setUser(data);
      else setUser(false);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only probe /auth/me on admin routes. Public pages don't need auth state
    // and this avoids 3x 401 requests polluting the console on every public page.
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (path.startsWith('/admin')) {
      fetchMe();
    } else {
      setUser(false);
      setLoading(false);
    }
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // ignore
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
